// src/pages/Contacts.js
import { useState, useMemo, useEffect } from "react";
import { Avatar, StageBadge, HealthBadge, LeadBadge, TagList, InvoicedDisplay, timeAgo } from "../components/UI";
import EmptyState from "../components/EmptyState";
import BrandLogo from "../components/BrandLogo";
import { Users } from "lucide-react";
import { contactsToCSV, watchContacts, watchScansCollection, CRM_COLLECTIONS, compareContacts, deleteContact } from "../lib/db";
import { contactHasScanned, enrichContactsWithScanRollup } from "../lib/contactScanRollup";
import { INDUSTRY_MAP, INDUSTRY_OPTIONS, LIFECYCLE_STAGES } from "../lib/constants";
import { contactProfileSubtitle, formatWorkRole } from "../lib/contactProfileFields";
import { isLidarLeadContact } from "../lib/deviceProfile";
import { normalizeLifecycleStage } from "../lib/lifecycleStages";
import { fmtDate } from "../components/UI";

const SORT_OPTIONS = [
  { id: "created_desc", label: "Created (newest first)" },
  { id: "created_asc", label: "Created (oldest first)" },
  { id: "updated_desc", label: "Updated (newest first)" },
  { id: "last_active_desc", label: "Last active (newest first)" },
  { id: "name_asc", label: "Name (A–Z)" },
  { id: "name_desc", label: "Name (Z–A)" },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "lidar", label: "Leads (LiDAR)" },
  { id: "has_scanned", label: "Has scanned" },
  { id: "uploaded_scans", label: "Uploaded scans" },
  { id: "trial_order", label: "Trial order" },
  { id: "paid_user", label: "Paid" },
  { id: "at_risk", label: "At-Risk" },
];

const EMPTY_COL_FILTERS = {
  industry: "",
  stage: "",
  invoiced: "",
  health: "",
  lead: "",
  tag: "",
  created: "",
};

const HEALTH_BUCKETS = [
  { id: "", label: "All health" },
  { id: "low", label: "Low (<40)" },
  { id: "medium", label: "Medium (40–69)" },
  { id: "high", label: "High (70+)" },
];

const LEAD_BUCKETS = [
  { id: "", label: "All lead" },
  { id: "low", label: "Low (<40)" },
  { id: "medium", label: "Medium (40–69)" },
  { id: "high", label: "High (70+)" },
];

const CREATED_BUCKETS = [
  { id: "", label: "All dates" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "90d", label: "Last 90 days" },
  { id: "older", label: "Older than 90d" },
];

function contactCreatedMs(c) {
  const ts = c.createdAt;
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate().getTime();
  const n = new Date(ts).getTime();
  return Number.isFinite(n) ? n : null;
}

function matchesScoreBucket(score, bucket) {
  const n = Number(score) || 0;
  if (!bucket) return true;
  if (bucket === "low") return n < 40;
  if (bucket === "medium") return n >= 40 && n < 70;
  if (bucket === "high") return n >= 70;
  return true;
}

function matchesCreatedBucket(c, bucket) {
  if (!bucket) return true;
  const ms = contactCreatedMs(c);
  if (ms == null) return bucket === "older";
  const age = Date.now() - ms;
  const day = 86400000;
  if (bucket === "7d") return age <= 7 * day;
  if (bucket === "30d") return age <= 30 * day;
  if (bucket === "90d") return age <= 90 * day;
  if (bucket === "older") return age > 90 * day;
  return true;
}

function ColumnHeaderFilter({ label, value, onChange, options, title }) {
  const active = Boolean(value);
  return (
    <th className={active ? "th-filter-active" : undefined} title={title || label}>
      <div className="th-filter">
        <span className="th-filter-label">{label}</span>
        <select
          className="th-filter-select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Filter by ${label}`}
        >
          {options.map((o) => (
            <option key={o.id || o.value || "all"} value={o.id ?? o.value ?? ""}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </th>
  );
}

export default function Contacts({ onOpenContact, onNewContact }) {
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [showMerge,setShowMerge]= useState(false);
  const [showImport,setShowImport]=useState(false);
  const [contacts, setContacts] = useState([]);
  const [scans, setScans] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [sortMode, setSortMode] = useState("created_desc");
  const [includeInternal, setIncludeInternal] = useState(false);
  const [colFilters, setColFilters] = useState(EMPTY_COL_FILTERS);
  const [deleting, setDeleting] = useState(false);

  function setColFilter(key, value) {
    setColFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearColFilters() {
    setColFilters(EMPTY_COL_FILTERS);
  }

  const hasColFilters = Object.values(colFilters).some(Boolean);

  useEffect(() => {
    return watchContacts(
      (data) => {
        setLoadError(null);
        setContacts(data);
      },
      () => setLoadError("Could not load contacts (check Firestore rules and indexes).")
    );
  }, []);

  useEffect(() => {
    return watchScansCollection(setScans, () => {});
  }, []);

  const enrichedContacts = useMemo(
    () => enrichContactsWithScanRollup(contacts, scans),
    [contacts, scans]
  );

  const visiblePool = useMemo(
    () => (includeInternal ? enrichedContacts : enrichedContacts.filter((c) => !c.isInternal)),
    [enrichedContacts, includeInternal]
  );

  const tagOptions = useMemo(() => {
    const set = new Set();
    visiblePool.forEach((c) => (c.tags || []).forEach((t) => set.add(t)));
    return [{ id: "", label: "All tags" }, ...[...set].sort().map((t) => ({ id: t, label: t }))];
  }, [visiblePool]);

  const filtered = useMemo(() => {
    return visiblePool.filter(c => {
      const q = search.toLowerCase();
      const matchSearch = !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || (c.companyName||"").toLowerCase().includes(q) || (c.workRole||"").toLowerCase().includes(q) || (c.location||"").toLowerCase().includes(q);
      const matchFilter =
        filter === "all" ? true :
        filter === "lidar" ? isLidarLeadContact(c) :
        filter === "at_risk" ? c.healthScore < 40 :
        filter === "has_scanned" ? contactHasScanned(c) :
        c.stage === filter || normalizeLifecycleStage(c.stage) === filter;
      if (!matchSearch || !matchFilter) return false;

      if (colFilters.industry && c.industry !== colFilters.industry) return false;
      if (colFilters.stage && normalizeLifecycleStage(c.stage) !== colFilters.stage) return false;
      if (colFilters.invoiced === "yes" && !(Number(c.lifetimeRevenue) > 0)) return false;
      if (colFilters.invoiced === "no" && Number(c.lifetimeRevenue) > 0) return false;
      if (!matchesScoreBucket(c.healthScore, colFilters.health)) return false;
      if (!matchesScoreBucket(c.leadScore, colFilters.lead)) return false;
      if (colFilters.tag && !(c.tags || []).includes(colFilters.tag)) return false;
      if (!matchesCreatedBucket(c, colFilters.created)) return false;
      return true;
    });
  }, [visiblePool, search, filter, colFilters]);

  const sortedContacts = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => compareContacts(a, b, sortMode));
    return copy;
  }, [filtered, sortMode]);

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === sortedContacts.length) setSelected(new Set());
    else setSelected(new Set(sortedContacts.map(c => c.id)));
  }

  function exportCSV() {
    const data = selected.size > 0 ? contacts.filter(c => selected.has(c.id)) : sortedContacts;
    const csv  = contactsToCSV(data);
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "scanbrix-contacts.csv"; a.click();
  }

  async function deleteSelected() {
    if (deleting || selected.size === 0) return;
    const ids = [...selected];
    const ok = window.confirm(
      `Delete ${ids.length} contact${ids.length === 1 ? "" : "s"}? This permanently removes the Firestore record and cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => deleteContact(id)));
      // Live `watchContacts` snapshot refreshes the table; just clear the selection.
      setSelected(new Set());
    } catch (err) {
      console.error("Failed to delete contacts", err);
      window.alert("Could not delete the selected contact(s). Check your Firestore permissions and try again.");
    } finally {
      setDeleting(false);
    }
  }

  const stageCounts = Object.fromEntries(FILTERS.map(f => [
    f.id,
    f.id === "all" ? visiblePool.length :
    f.id === "at_risk" ? visiblePool.filter(c => c.healthScore < 40).length :
    f.id === "lidar" ? visiblePool.filter(isLidarLeadContact).length :
    f.id === "has_scanned" ? visiblePool.filter(contactHasScanned).length :
    visiblePool.filter(c => normalizeLifecycleStage(c.stage) === f.id).length
  ]));

  return (
    <div>
      {loadError && (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 9, background: "var(--red-dim)", color: "var(--red)", fontSize: 13 }}>
          {loadError}
        </div>
      )}
      {!loadError && contacts.length === 0 && (
        <div
          className="card"
          style={{ padding: 18, marginBottom: 16, fontSize: 13, color: "var(--text-2)", lineHeight: 1.55 }}
        >
          <div style={{ fontWeight: 600, color: "var(--text-1)", marginBottom: 8 }}>No contacts in Firestore</div>
          <p style={{ margin: "0 0 10px" }}>
            The CRM reads Firestore collection <code style={{ color: "var(--accent)" }}>{CRM_COLLECTIONS.people}</code> — not the
            Authentication user list. Those are different: Auth holds sign-ins; Firestore only shows rows your app has written (often{" "}
            <code>users</code> with document id = Auth uid).
          </p>
          <p style={{ margin: "0 0 10px" }}>
            If your profiles live under another collection name, set in <code>.env.local</code>:
          </p>
          <pre
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 8,
              background: "var(--bg-elevated)",
              fontSize: 12,
              overflow: "auto",
            }}
          >
            {`REACT_APP_CRM_CONTACTS_COLLECTION=your_collection_name\nREACT_APP_CRM_COMPANIES_COLLECTION=companies`}
          </pre>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--text-3)" }}>
            Rebuild and redeploy. Add a matching <code>match /your_collection/{"{"}id{"}"}</code> in <code>firestore.rules</code> if
            needed, then <code>npm run deploy:rules</code>. If Firestore has no user documents yet, add a Cloud Function on Auth
            create (or backfill) to write each user into this collection.
          </p>
        </div>
      )}
      {/* Search + actions — see `.contacts-toolbar` in styles.css for mobile layout */}
      <div className="contacts-toolbar">
        <div className="search-bar contacts-toolbar-search">
          <BrandLogo className="search-logo logo-mark" width={24} height={24} alt="" />
          <input
            placeholder="Search by name, email, or company..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <span style={{cursor:"pointer",color:"var(--text-3)",fontSize:14}} onClick={()=>setSearch("")}>×</span>}
        </div>
        <div className="contacts-toolbar-actions">
          <button type="button" className="btn contacts-toolbar-btn" onClick={()=>setShowImport(true)}>↑ Import</button>
          <button type="button" className="btn contacts-toolbar-btn" onClick={exportCSV}>↓ Export</button>
          <button
            type="button"
            className="btn btn-primary contacts-toolbar-primary"
            onClick={() => onNewContact?.()}
          >
            + New contact
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        {FILTERS.map(f => (
          <div
            key={f.id}
            className={`filter-chip ${filter === f.id ? "active" : ""}`}
            onClick={() => setFilter(f.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setFilter(f.id))}
          >
            {f.label}
            {stageCounts[f.id] > 0 && <span style={{marginLeft:5,opacity:0.7}}>{stageCounts[f.id]}</span>}
          </div>
        ))}
      </div>

      <div
        style={{
          fontSize: 12,
          color: "var(--text-3)",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
            style={{ accentColor: "var(--accent)" }}
          />
          <span style={{ color: "var(--text-3)" }}>Show internal accounts</span>
        </label>
        <span style={{ opacity: 0.6 }}>·</span>
        <span>
          Showing <strong style={{ color: "var(--text-2)" }}>{sortedContacts.length}</strong> of {visiblePool.length} in view
          {includeInternal ? ` (${contacts.length} total)` : ""}
        </span>
        {hasColFilters ? (
          <>
            <span style={{ opacity: 0.6 }}>·</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearColFilters} style={{ padding: "2px 8px", fontSize: 12 }}>
              Clear column filters
            </button>
          </>
        ) : null}
        <span style={{ opacity: 0.6 }}>·</span>
        <label className="contacts-sort-label" style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <span style={{ color: "var(--text-3)" }}>Sort by</span>
          <select
            className="input contacts-sort-select"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={{ marginBottom: 0, minWidth: 200, padding: "6px 10px", fontSize: 12 }}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{
          display:"flex", alignItems:"center", gap:10, padding:"10px 16px",
          background:"var(--accent-dim)", border:"0.5px solid var(--accent-mid)",
          borderRadius:9, marginBottom:12, flexWrap:"wrap"
        }}>
          <span style={{fontSize:13, color:"var(--accent)", fontWeight:500}}>{selected.size} selected</span>
          <div style={{display:"flex", gap:6, marginLeft:"auto"}}>
            <button className="btn btn-sm">Add tag</button>
            <button className="btn btn-sm">Change stage</button>
            <button className="btn btn-sm">Assign rep</button>
            <button className="btn btn-sm" onClick={exportCSV}>Export</button>
            <button className="btn btn-sm btn-danger" onClick={deleteSelected} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={()=>setSelected(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card contacts-table-card" style={{padding:0}}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{width:36}}>
                  <input type="checkbox"
                    checked={selected.size === sortedContacts.length && sortedContacts.length > 0}
                    onChange={toggleAll}
                    style={{cursor:"pointer", accentColor:"var(--accent)"}}
                  />
                </th>
                <th>Contact</th>
                <ColumnHeaderFilter
                  label="Industry"
                  value={colFilters.industry}
                  onChange={(v) => setColFilter("industry", v)}
                  options={[{ id: "", label: "All industries" }, ...INDUSTRY_OPTIONS]}
                />
                <ColumnHeaderFilter
                  label="Stage"
                  value={colFilters.stage}
                  onChange={(v) => setColFilter("stage", v)}
                  options={[{ id: "", label: "All stages" }, ...LIFECYCLE_STAGES.map((s) => ({ id: s.id, label: s.label }))]}
                />
                <ColumnHeaderFilter
                  label="Invoiced"
                  value={colFilters.invoiced}
                  onChange={(v) => setColFilter("invoiced", v)}
                  options={[
                    { id: "", label: "All" },
                    { id: "yes", label: "Has invoiced" },
                    { id: "no", label: "No invoiced" },
                  ]}
                />
                <ColumnHeaderFilter
                  label="Health"
                  value={colFilters.health}
                  onChange={(v) => setColFilter("health", v)}
                  options={HEALTH_BUCKETS}
                  title="Engagement / retention risk"
                />
                <ColumnHeaderFilter
                  label="Lead"
                  value={colFilters.lead}
                  onChange={(v) => setColFilter("lead", v)}
                  options={LEAD_BUCKETS}
                  title="Conversion / expansion potential"
                />
                <ColumnHeaderFilter
                  label="Tags"
                  value={colFilters.tag}
                  onChange={(v) => setColFilter("tag", v)}
                  options={tagOptions}
                />
                <ColumnHeaderFilter
                  label="Created"
                  value={colFilters.created}
                  onChange={(v) => setColFilter("created", v)}
                  options={CREATED_BUCKETS}
                />
                <th>Last active</th>
              </tr>
            </thead>
            <tbody>
              {sortedContacts.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <EmptyState
                      icon={Users}
                      title="No contacts found"
                      description={search || filter !== "all" ? "Try adjusting your search or filters." : "Add your first contact to get started."}
                      action={
                        !search && filter === "all" ? (
                          <button type="button" className="btn btn-primary" onClick={() => onNewContact?.()}>
                            + New contact
                          </button>
                        ) : null
                      }
                    />
                  </td>
                </tr>
              ) : sortedContacts.map(c => {
                const lastMs = c.lastActiveAt?.toDate?.()?.getTime?.() ?? (c.lastActiveAt ? new Date(c.lastActiveAt).getTime() : null);
                const activeRecently = lastMs != null && Date.now() - lastMs < 7 * 86400000;
                const stale = lastMs != null && Date.now() - lastMs > 14 * 86400000;
                return (
                <tr
                  key={c.id}
                  className={selected.has(c.id) ? "row-selected" : ""}
                  onClick={() => onOpenContact(c)}
                >
                  <td onClick={e => { e.stopPropagation(); toggleSelect(c.id); }}>
                    <input type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => {}}
                      style={{cursor:"pointer", accentColor:"var(--accent)"}}
                    />
                  </td>
                  <td>
                    <div className="contact-row-meta">
                      <span
                        className={`contact-activity-dot ${activeRecently ? "contact-activity-dot--active" : stale ? "contact-activity-dot--stale" : ""}`}
                        title={c.lastActiveAt ? timeAgo(c.lastActiveAt) : "No activity"}
                      />
                      <Avatar name={c.name} size="sm" />
                      <div>
                        <div className="contact-row-name">
                        {c.name}
                        {includeInternal && c.isInternal ? (
                          <span className="badge badge-muted" style={{ marginLeft: 8, fontSize: 10 }}>
                            Internal
                          </span>
                        ) : null}
                      </div>
                        <div className="contact-row-email">{c.email}</div>
                        {contactProfileSubtitle(c) ? (
                          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                            {contactProfileSubtitle(c)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                    {c.industry
                      ? INDUSTRY_MAP[c.industry] || c.industry
                      : formatWorkRole(c.workRole, c.workRoleOther) || "—"}
                  </td>
                  <td><StageBadge stage={c.displayStage ?? c.stage} /></td>
                  <td><InvoicedDisplay contact={c} /></td>
                  <td><HealthBadge score={c.healthScore} /></td>
                  <td><LeadBadge score={c.leadScore} /></td>
                  <td><TagList tags={c.tags} max={2} /></td>
                  <td style={{ color: "var(--text-2)", fontSize: 12 }} title={c.createdAt?.toDate?.()?.toISOString?.() || ""}>
                    {fmtDate(c.createdAt)}
                  </td>
                  <td style={{color:"var(--text-3)",fontSize:12}}>
                    {c.lastActiveAt ? timeAgo(c.lastActiveAt) : "—"}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12}}>
        <span style={{fontSize:12, color:"var(--text-3)"}}>
          {sortedContacts.length} of {visiblePool.length} in view · {SORT_OPTIONS.find((o) => o.id === sortMode)?.label || sortMode}
        </span>
        <button className="btn btn-sm" onClick={()=>setShowMerge(true)}>
          ⊕ Find duplicates
        </button>
      </div>

      {/* CSV Import Modal */}
      {showImport && (
        <div className="modal-overlay" onClick={()=>setShowImport(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Import contacts from CSV</div>
              <button className="btn btn-ghost btn-icon" onClick={()=>setShowImport(false)}>×</button>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{
                border:"1.5px dashed var(--border-md)", borderRadius:10,
                padding:"32px 20px", textAlign:"center", cursor:"pointer",
                color:"var(--text-3)"
              }}>
                <div style={{fontSize:28,marginBottom:10}}>↑</div>
                <div style={{fontWeight:500,color:"var(--text-2)",marginBottom:4}}>Drop your CSV here or click to browse</div>
                <div style={{fontSize:12}}>Columns: Name, Email, Company, Stage, MRR, Tags</div>
              </div>
            </div>
            <div style={{fontSize:12,color:"var(--text-3)",marginBottom:16}}>
              <strong style={{color:"var(--text-2)"}}>Expected columns:</strong> Name, Email, Company, Stage, MRR, Tags (semicolon-separated)
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={()=>setShowImport(false)}>Cancel</button>
              <button className="btn btn-primary">Import contacts</button>
            </div>
          </div>
        </div>
      )}

      {/* Merge / duplicates modal */}
      {showMerge && (
        <div className="modal-overlay" onClick={()=>setShowMerge(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Find and merge duplicates</div>
              <button className="btn btn-ghost btn-icon" onClick={()=>setShowMerge(false)}>×</button>
            </div>
            <div style={{fontSize:13,color:"var(--text-2)",marginBottom:16}}>
              Scans for contacts with matching email addresses. You choose which record to keep.
            </div>
            <div style={{
              padding:"24px",textAlign:"center",
              background:"var(--bg-elevated)",borderRadius:9,
              color:"var(--text-3)"
            }}>
              <div style={{fontSize:22,marginBottom:8}}>⊕</div>
              No duplicates found in your current contacts.
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={()=>setShowMerge(false)}>Close</button>
              <button className="btn btn-primary">Scan for duplicates</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
